import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Plus, ChevronLeft, ChevronRight, Trash2, Pencil, FileDown, Copy, Sparkles, Loader2, RotateCcw, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function startOfWeek(d) {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7; // Mon=0
    x.setDate(x.getDate() - day);
    x.setHours(0, 0, 0, 0);
    return x;
}
function fmt(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const SHIFT_ROLES = [
    "Cameriere",
    "Cameriere senior",
    "Maître",
    "Sommelier",
    "Barman",
    "Chef",
    "Sous-chef",
    "Pizzaiolo",
    "Pasticciere",
    "Lavapiatti",
    "Hostess",
    "Runner",
];

const SHIFT_LOCATIONS = ["Sala", "Cucina", "Magazzino", "Bancone bar", "Pizzeria"];

// Color matrix: warm (early shift, before 16:00) vs cool (evening, from 16:00) ×
// section (Sala, Cucina, Bancone bar, Pizzeria, altro)
const SHIFT_COLORS = {
    warm: {
        Sala: "bg-orange-100 border-orange-400 text-orange-900",
        Cucina: "bg-red-100 border-red-500 text-red-900",
        "Bancone bar": "bg-amber-100 border-amber-500 text-amber-900",
        Pizzeria: "bg-yellow-100 border-yellow-500 text-yellow-900",
        default: "bg-rose-50 border-rose-300 text-rose-900",
    },
    cool: {
        Sala: "bg-blue-100 border-blue-500 text-blue-900",
        Cucina: "bg-emerald-100 border-emerald-500 text-emerald-900",
        "Bancone bar": "bg-cyan-100 border-cyan-500 text-cyan-900",
        Pizzeria: "bg-violet-100 border-violet-500 text-violet-900",
        default: "bg-sky-50 border-sky-300 text-sky-900",
    },
};

const colorForShift = (shift) => {
    let isWarm = true;
    try {
        const [h] = (shift.start_time || "00:00").split(":").map(Number);
        isWarm = h < 16;
    } catch (_e) {
        // fallback warm
    }
    const palette = isWarm ? SHIFT_COLORS.warm : SHIFT_COLORS.cool;
    const loc = shift.location || "default";
    return palette[loc] || palette.default;
};

const empty = {
    employee_id: "",
    dates: [],
    start_time: "18:00",
    end_time: "23:00",
    double_shift: false,
    start_time2: "12:00",
    end_time2: "15:00",
    role: "",
    location: "Sala",
    notes: "",
};

export default function ShiftsPage() {
    const [employees, setEmployees] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [view, setView] = useState("calendar");
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [filterEmployee, setFilterEmployee] = useState("");
    const [copyOpen, setCopyOpen] = useState(false);
    const [copyOverwrite, setCopyOverwrite] = useState(false);
    const [optOpen, setOptOpen] = useState(false);
    const [optStep, setOptStep] = useState(1); // 1=covers, 2=preview
    const [optLoading, setOptLoading] = useState(false);
    const [optCovers, setOptCovers] = useState({}); // {date: {colazione, pranzo, aperitivo, cena}}
    const [optResult, setOptResult] = useState(null);
    const [optOverwrite, setOptOverwrite] = useState(true);
    const [backups, setBackups] = useState([]);
    const [restoreOpen, setRestoreOpen] = useState(false);

    const loadBackups = async () => {
        try {
            const { data } = await api.get("/shifts/backups");
            setBackups(data.items || []);
        } catch (_e) {
            // Silent fail on first load (e.g., not authenticated)
            setBackups([]);
        }
    };

    const restoreBackup = async (backupId) => {
        try {
            const { data } = await api.post(`/shifts/restore/${backupId}`);
            toast.success(
                `${data.count} turni ripristinati (settimana ${data.week_start})`,
            );
            // Navigate to that week
            try {
                const [y, m, dd] = data.week_start.split("-").map(Number);
                setWeekStart(new Date(y, m - 1, dd));
            } catch (_err) {
                // ignore parse error
            }
            setRestoreOpen(false);
            loadShifts();
            loadBackups();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const loadEmployees = async () => {
        const { data } = await api.get("/employees");
        setEmployees(data.items.filter((i) => i.status === "active"));
    };
    const loadShifts = async () => {
        const start = fmt(weekStart);
        const end = fmt(addDays(weekStart, 6));
        const { data } = await api.get("/shifts", { params: { start, end } });
        setShifts(data.items);
    };

    useEffect(() => {
        loadEmployees();
        loadBackups();
    }, []);
    useEffect(() => {
        loadShifts();
    }, [weekStart]);

    const openNewAt = (date) => {
        setForm({ ...empty, dates: date ? [date] : [] });
        setOpen(true);
    };

    const toggleDate = (d) => {
        setForm((f) => {
            const has = f.dates.includes(d);
            return {
                ...f,
                dates: has ? f.dates.filter((x) => x !== d) : [...f.dates, d],
            };
        });
    };

    const selectAllWeek = () => {
        setForm((f) => ({ ...f, dates: days.map((d) => fmt(d)) }));
    };
    const clearDates = () => setForm((f) => ({ ...f, dates: [] }));

    const save = async () => {
        try {
            if (!form.employee_id) {
                toast.error("Seleziona un dipendente");
                return;
            }
            if (!form.dates || form.dates.length === 0) {
                toast.error("Seleziona almeno un giorno");
                return;
            }
            const payload = {
                employee_id: form.employee_id,
                dates: form.dates,
                start_time: form.start_time,
                end_time: form.end_time,
                role: form.role,
                location: form.location,
                notes: form.notes,
            };
            if (form.double_shift) {
                payload.start_time2 = form.start_time2;
                payload.end_time2 = form.end_time2;
            }
            const { data } = await api.post("/shifts/bulk", payload);
            toast.success(
                data.count > 1
                    ? `${data.count} turni creati`
                    : "Turno creato",
            );
            setOpen(false);
            loadShifts();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/shifts/${id}`);
            toast.success("Turno eliminato");
            loadShifts();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const openOptimize = () => {
        const init = {};
        days.forEach((d) => {
            init[fmt(d)] = { colazione: 0, pranzo: 0, aperitivo: 0, cena: 0 };
        });
        setOptCovers(init);
        setOptResult(null);
        setOptStep(1);
        setOptOpen(true);
    };

    const generateOptimization = async () => {
        setOptLoading(true);
        try {
            const payload = {
                week_start: fmt(weekStart),
                days: Object.entries(optCovers).map(([date, s]) => ({
                    date,
                    colazione: s.colazione || 0,
                    pranzo: s.pranzo || 0,
                    aperitivo: s.aperitivo || 0,
                    cena: s.cena || 0,
                })),
            };
            const { data } = await api.post("/shifts/optimize", payload);
            setOptResult(data);
            setOptStep(2);
            toast.success("Piano turni generato");
        } catch (e) {
            toast.error(
                formatApiError(e.response?.data?.detail) ||
                    "Errore generazione piano",
            );
        } finally {
            setOptLoading(false);
        }
    };

    const applyOptimization = async () => {
        if (!optResult) return;
        setOptLoading(true);
        try {
            const { data } = await api.post("/shifts/optimize/apply", {
                shifts: optResult.shifts,
                overwrite_week: optOverwrite ? fmt(weekStart) : null,
            });
            toast.success(`${data.count} turni applicati al calendario`);
            setOptOpen(false);
            loadShifts();
            loadBackups();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setOptLoading(false);
        }
    };

    const copyWeekToNext = async () => {
        try {
            const src_start = fmt(weekStart);
            const src_end = fmt(addDays(weekStart, 6));
            const tgt_start = fmt(addDays(weekStart, 7));
            const tgt_end = fmt(addDays(weekStart, 13));
            const { data } = await api.post("/shifts/copy-week", {
                source_start: src_start,
                source_end: src_end,
                target_start: tgt_start,
                target_end: tgt_end,
                overwrite: copyOverwrite,
            });
            toast.success(
                `${data.count} turni copiati alla settimana successiva`,
            );
            setCopyOpen(false);
            // Auto-navigate to target week to show the result
            setWeekStart(addDays(weekStart, 7));
            loadBackups();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const exportCSV = () => {
        try {
            const items = visibleShifts;
            if (items.length === 0) {
                toast.error("Nessun turno da esportare");
                return;
            }
            const header = [
                "date",
                "employee_id",
                "employee_name",
                "start_time",
                "end_time",
                "role",
                "location",
                "notes",
            ];
            const esc = (v) => {
                if (v == null) return "";
                const s = String(v).replace(/"/g, '""');
                return /[",\n;]/.test(s) ? `"${s}"` : s;
            };
            const rows = items
                .slice()
                .sort((a, b) =>
                    a.date === b.date
                        ? a.start_time.localeCompare(b.start_time)
                        : a.date.localeCompare(b.date),
                )
                .map((s) =>
                    header
                        .map((k) => esc(s[k] ?? ""))
                        .join(","),
                );
            const csv = [header.join(","), ...rows].join("\n");
            // BOM for Excel UTF-8 compatibility
            const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `turni_${fmt(weekStart)}_${fmt(addDays(weekStart, 6))}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(`${items.length} turni esportati`);
        } catch (e) {
            console.error(e);
            toast.error("Errore esportazione CSV");
        }
    };

    const importCSVFromFile = async (file) => {
        try {
            const text = await file.text();
            // Strip BOM
            const clean = text.replace(/^\uFEFF/, "");
            const lines = clean.split(/\r?\n/).filter((l) => l.trim());
            if (lines.length < 2) {
                toast.error("CSV vuoto o senza dati");
                return;
            }
            // Robust CSV parser (handles quoted fields with commas)
            const parseLine = (line) => {
                const out = [];
                let cur = "";
                let inQ = false;
                for (let i = 0; i < line.length; i++) {
                    const c = line[i];
                    if (inQ) {
                        if (c === '"' && line[i + 1] === '"') {
                            cur += '"';
                            i++;
                        } else if (c === '"') {
                            inQ = false;
                        } else {
                            cur += c;
                        }
                    } else {
                        if (c === '"') inQ = true;
                        else if (c === ",") {
                            out.push(cur);
                            cur = "";
                        } else cur += c;
                    }
                }
                out.push(cur);
                return out;
            };
            const headers = parseLine(lines[0]).map((h) => h.trim().toLowerCase());
            const rows = lines.slice(1).map((line) => {
                const cells = parseLine(line);
                const obj = {};
                headers.forEach((h, i) => {
                    obj[h] = (cells[i] || "").trim();
                });
                return obj;
            });
            const valid = rows.filter((r) => r.date && (r.employee_id || r.employee_name));
            if (valid.length === 0) {
                toast.error("Nessuna riga valida (servono date + employee_id o employee_name)");
                return;
            }
            const confirm = window.confirm(
                `Importare ${valid.length} turni? \n\n` +
                    `Confermando "OK" verranno SOSTITUITI i turni della settimana ${fmt(weekStart)} → ${fmt(addDays(weekStart, 6))} prima dell'import (backup creato automaticamente).\n\n` +
                    `Annulla per importare SENZA cancellare i turni esistenti (rischio doppioni).`,
            );
            const overwrite_week = confirm ? fmt(weekStart) : null;
            const { data } = await api.post("/shifts/import", {
                shifts: valid,
                overwrite_week,
            });
            if (data.skipped_count > 0) {
                toast.warning(
                    `${data.count} importati, ${data.skipped_count} saltati (dipendenti non trovati)`,
                );
            } else {
                toast.success(`${data.count} turni importati`);
            }
            loadShifts();
            loadBackups();
        } catch (e) {
            console.error(e);
            toast.error(
                formatApiError(e.response?.data?.detail) ||
                    "Errore importazione CSV",
            );
        }
    };

    const exportPDF = (singleEmployeeId = null) => {
        try {
            const targetEmpId = singleEmployeeId || filterEmployee || null;
            const items = shifts.filter(
                (s) => !targetEmpId || s.employee_id === targetEmpId,
            );
            if (items.length === 0) {
                toast.error("Nessun turno da esportare");
                return;
            }
            const start = fmt(weekStart);
            const end = fmt(addDays(weekStart, 6));
            const dayList = days;

            const byEmp = {};
            items.forEach((s) => {
                if (!byEmp[s.employee_id])
                    byEmp[s.employee_id] = {
                        name: s.employee_name,
                        days: {},
                    };
                if (!byEmp[s.employee_id].days[s.date])
                    byEmp[s.employee_id].days[s.date] = [];
                byEmp[s.employee_id].days[s.date].push(
                    `${s.start_time}-${s.end_time}${s.location ? ` · ${s.location}` : ""}`,
                );
            });

            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4",
            });
            const dayLabels = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
            doc.setFontSize(16);
            const title = targetEmpId
                ? `Turni settimanali — ${byEmp[targetEmpId]?.name || ""}`
                : "Turni settimanali — Tutto il personale";
            doc.text(title, 14, 14);
            doc.setFontSize(10);
            doc.text(`Settimana: ${start} → ${end}`, 14, 20);

            const head = [
                [
                    "Dipendente",
                    ...dayList.map(
                        (d, i) => `${dayLabels[i]} ${d.getDate()}/${d.getMonth() + 1}`,
                    ),
                ],
            ];
            const body = Object.values(byEmp).map((e) => [
                e.name,
                ...dayList.map(
                    (d) => (e.days[fmt(d)] || []).join("\n") || "—",
                ),
            ]);

            autoTable(doc, {
                head,
                body,
                startY: 25,
                styles: { fontSize: 8, cellPadding: 2, valign: "top" },
                headStyles: { fillColor: [10, 35, 66], textColor: 255 },
                columnStyles: { 0: { fontStyle: "bold", cellWidth: 35 } },
                theme: "grid",
                margin: { left: 10, right: 10 },
            });

            const empSlug = targetEmpId
                ? `_${(byEmp[targetEmpId]?.name || "dip").replace(/\s+/g, "_")}`
                : "";
            doc.save(`turni_${start}_${end}${empSlug}.pdf`);
            toast.success("PDF generato");
        } catch (e) {
            console.error(e);
            toast.error("Errore generazione PDF");
        }
    };

    const openEdit = (s) => {
        setEditForm({
            id: s.id,
            employee_id: s.employee_id,
            date: s.date,
            start_time: s.start_time,
            end_time: s.end_time,
            role: s.role || "",
            location: s.location || "Sala",
            notes: s.notes || "",
        });
        setEditOpen(true);
    };

    const saveEdit = async () => {
        if (!editForm) return;
        try {
            const { id, ...payload } = editForm;
            await api.put(`/shifts/${id}`, payload);
            toast.success("Turno aggiornato");
            setEditOpen(false);
            setEditForm(null);
            loadShifts();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const visibleShifts = filterEmployee
        ? shifts.filter((s) => s.employee_id === filterEmployee)
        : shifts;

    return (
        <div data-testid="shifts-page">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Pianificazione</div>
                    <h1 className="font-display text-4xl text-navy">Turni</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex bg-white border border-sf rounded-md p-1">
                        <button
                            className={`px-3 py-1.5 text-sm rounded ${view === "calendar" ? "bg-navy text-white" : "text-sf-secondary"}`}
                            onClick={() => setView("calendar")}
                            data-testid="shifts-view-calendar"
                        >
                            Calendario
                        </button>
                        <button
                            className={`px-3 py-1.5 text-sm rounded ${view === "list" ? "bg-navy text-white" : "text-sf-secondary"}`}
                            onClick={() => setView("list")}
                            data-testid="shifts-view-list"
                        >
                            Tabella
                        </button>
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => setWeekStart(addDays(weekStart, -7))}
                        data-testid="shifts-prev-week"
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <div
                        className="px-4 py-2 bg-white border border-sf rounded-md text-sm font-medium"
                        data-testid="shifts-week-label"
                    >
                        {weekStart.toLocaleDateString("it-IT", {
                            day: "numeric",
                            month: "short",
                        })}{" "}
                        →{" "}
                        {addDays(weekStart, 6).toLocaleDateString("it-IT", {
                            day: "numeric",
                            month: "short",
                        })}
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => setWeekStart(addDays(weekStart, 7))}
                        data-testid="shifts-next-week"
                    >
                        <ChevronRight size={16} />
                    </Button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="bg-navy text-white lift"
                                onClick={() => openNewAt(fmt(new Date()))}
                                data-testid="shifts-add-btn"
                            >
                                <Plus size={16} className="mr-2" /> Nuovo turno
                            </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="shift-dialog" className="max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">
                                    Nuovo turno
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <Field label="Dipendente">
                                    <Select
                                        value={form.employee_id}
                                        onValueChange={(v) => {
                                            const emp = employees.find((e) => e.id === v);
                                            setForm({
                                                ...form,
                                                employee_id: v,
                                                location: emp?.default_zone || form.location || "Sala",
                                                role: emp?.role || form.role,
                                            });
                                        }}
                                    >
                                        <SelectTrigger data-testid="shift-form-employee">
                                            <SelectValue placeholder="Seleziona…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {employees.map((e) => (
                                                <SelectItem
                                                    key={e.id}
                                                    value={e.id}
                                                >
                                                    {e.emoji || "👤"} {e.name} — {e.role}
                                                    {e.default_zone ? ` (${e.default_zone})` : ""}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Giorni (seleziona uno o più)">
                                    <div className="space-y-2">
                                        <div className="flex flex-wrap gap-2">
                                            {days.map((d, i) => {
                                                const key = fmt(d);
                                                const active =
                                                    form.dates.includes(key);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={key}
                                                        onClick={() =>
                                                            toggleDate(key)
                                                        }
                                                        className={`px-3 py-2 rounded-md border text-xs font-medium transition ${
                                                            active
                                                                ? "bg-navy text-white border-navy"
                                                                : "bg-white text-sf-secondary border-sf hover:border-navy/40"
                                                        }`}
                                                        data-testid={`shift-form-day-${key}`}
                                                    >
                                                        <div className="text-[10px] uppercase tracking-wider opacity-80">
                                                            {DAYS[i]}
                                                        </div>
                                                        <div className="text-sm font-display">
                                                            {d.getDate()}/
                                                            {d.getMonth() + 1}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs">
                                            <button
                                                type="button"
                                                onClick={selectAllWeek}
                                                className="text-amber-brand hover:underline"
                                                data-testid="shift-form-select-all"
                                            >
                                                Seleziona tutta la settimana
                                            </button>
                                            <button
                                                type="button"
                                                onClick={clearDates}
                                                className="text-sf-muted hover:underline"
                                                data-testid="shift-form-clear"
                                            >
                                                Azzera
                                            </button>
                                            <span className="ml-auto text-sf-muted">
                                                {form.dates.length} selezionati
                                            </span>
                                        </div>
                                        <div>
                                            <Label className="text-[10px] uppercase tracking-wider text-sf-muted">
                                                Aggiungi data extra
                                            </Label>
                                            <Input
                                                type="date"
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    if (
                                                        v &&
                                                        !form.dates.includes(v)
                                                    )
                                                        setForm((f) => ({
                                                            ...f,
                                                            dates: [
                                                                ...f.dates,
                                                                v,
                                                            ],
                                                        }));
                                                    e.target.value = "";
                                                }}
                                                data-testid="shift-form-date-extra"
                                                className="mt-1"
                                            />
                                        </div>
                                        {form.dates.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 pt-1">
                                                {form.dates
                                                    .slice()
                                                    .sort()
                                                    .map((d) => (
                                                        <span
                                                            key={d}
                                                            className="inline-flex items-center gap-1 bg-amber-brand/15 border border-amber-brand/40 text-navy text-[11px] px-2 py-0.5 rounded"
                                                        >
                                                            {d}
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    toggleDate(
                                                                        d,
                                                                    )
                                                                }
                                                                className="opacity-60 hover:opacity-100"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Inizio">
                                        <Input
                                            type="time"
                                            value={form.start_time}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    start_time: e.target.value,
                                                })
                                            }
                                            data-testid="shift-form-start"
                                        />
                                    </Field>
                                    <Field label="Fine">
                                        <Input
                                            type="time"
                                            value={form.end_time}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    end_time: e.target.value,
                                                })
                                            }
                                            data-testid="shift-form-end"
                                        />
                                    </Field>
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                    <input
                                        type="checkbox"
                                        id="double-shift"
                                        checked={form.double_shift}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                double_shift: e.target.checked,
                                            })
                                        }
                                        data-testid="shift-form-double"
                                        className="w-4 h-4 accent-navy"
                                    />
                                    <label
                                        htmlFor="double-shift"
                                        className="text-sm text-sf-secondary cursor-pointer"
                                    >
                                        Doppio turno (es. pranzo + cena)
                                    </label>
                                </div>
                                {form.double_shift && (
                                    <div className="grid grid-cols-2 gap-3 bg-amber-brand/10 border border-amber-brand/30 rounded-md p-3">
                                        <Field label="2° turno - Inizio">
                                            <Input
                                                type="time"
                                                value={form.start_time2}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        start_time2: e.target.value,
                                                    })
                                                }
                                                data-testid="shift-form-start2"
                                            />
                                        </Field>
                                        <Field label="2° turno - Fine">
                                            <Input
                                                type="time"
                                                value={form.end_time2}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        end_time2: e.target.value,
                                                    })
                                                }
                                                data-testid="shift-form-end2"
                                            />
                                        </Field>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Ruolo turno">
                                        <Select
                                            value={form.role || ""}
                                            onValueChange={(v) =>
                                                setForm({ ...form, role: v })
                                            }
                                        >
                                            <SelectTrigger data-testid="shift-form-role">
                                                <SelectValue placeholder="Seleziona ruolo" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SHIFT_ROLES.map((r) => (
                                                    <SelectItem key={r} value={r}>
                                                        {r}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Postazione">
                                        <Select
                                            value={form.location || "Sala"}
                                            onValueChange={(v) =>
                                                setForm({ ...form, location: v })
                                            }
                                        >
                                            <SelectTrigger data-testid="shift-form-location">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SHIFT_LOCATIONS.map((z) => (
                                                    <SelectItem key={z} value={z}>
                                                        {z}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setOpen(false)}
                                >
                                    Annulla
                                </Button>
                                <Button
                                    className="bg-navy text-white"
                                    onClick={save}
                                    data-testid="shift-form-save"
                                >
                                    Salva turno
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Secondary toolbar: filter by employee + PDF export */}
            <div className="bg-white border border-sf rounded-xl p-3 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 flex-1">
                    <Label className="text-xs uppercase tracking-wider text-sf-muted whitespace-nowrap">
                        Filtra dipendente
                    </Label>
                    <Select
                        value={filterEmployee || "all"}
                        onValueChange={(v) =>
                            setFilterEmployee(v === "all" ? "" : v)
                        }
                    >
                        <SelectTrigger
                            className="max-w-[260px]"
                            data-testid="shifts-filter-employee"
                        >
                            <SelectValue placeholder="Tutti i dipendenti" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tutti i dipendenti</SelectItem>
                            {employees.map((e) => (
                                <SelectItem key={e.id} value={e.id}>
                                    {e.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={openOptimize}
                        data-testid="shifts-optimize"
                        className="bg-gradient-to-r from-amber-brand to-amber-brand/80 text-navy hover:opacity-90 font-medium"
                    >
                        <Sparkles size={14} className="mr-2" />
                        Ottimizza AI
                    </Button>
                    {backups.length > 0 && (
                        <Button
                            variant="outline"
                            onClick={() => setRestoreOpen(true)}
                            data-testid="shifts-restore"
                            className="border-sf-secondary text-sf-secondary"
                            title="Ripristina turni dal backup"
                        >
                            <RotateCcw size={14} className="mr-2" />
                            Ripristina ({backups.length})
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={() => setCopyOpen(true)}
                        data-testid="shifts-copy-week"
                        className="border-amber-brand text-amber-brand"
                        disabled={shifts.length === 0}
                        title={shifts.length === 0 ? "Nessun turno da copiare" : "Copia tutti i turni alla settimana successiva"}
                    >
                        <Copy size={14} className="mr-2" />
                        Copia settimana
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => exportPDF()}
                        data-testid="shifts-pdf-all"
                        className="border-navy text-navy"
                    >
                        <FileDown size={14} className="mr-2" />
                        PDF turni{filterEmployee ? " (filtrati)" : " (tutti)"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={exportCSV}
                        data-testid="shifts-csv-export"
                        className="border-sf-secondary text-sf-secondary"
                        disabled={visibleShifts.length === 0}
                    >
                        <Download size={14} className="mr-2" />
                        Salva CSV
                    </Button>
                    <label
                        className="inline-flex items-center gap-2 cursor-pointer border border-sf-secondary text-sf-secondary px-3 py-2 rounded-md text-sm hover:bg-[#fafaf7] font-medium"
                        data-testid="shifts-csv-import-label"
                    >
                        <Upload size={14} />
                        Importa CSV
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            data-testid="shifts-csv-import-input"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) importCSVFromFile(f);
                                e.target.value = "";
                            }}
                        />
                    </label>
                    {filterEmployee && (
                        <Button
                            variant="outline"
                            onClick={() => exportPDF(filterEmployee)}
                            data-testid="shifts-pdf-single"
                            className="border-amber-brand text-amber-brand"
                        >
                            <FileDown size={14} className="mr-2" />
                            Stampa singola
                        </Button>
                    )}
                </div>
            </div>

            {view === "calendar" ? (
                <>
                <div className="mb-3 bg-white border border-sf rounded-md p-3 text-xs">
                    <div className="font-medium text-navy mb-1.5">Legenda colori turni</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                            { l: "Sala", w: "bg-orange-100 border-orange-400", c: "bg-blue-100 border-blue-500" },
                            { l: "Cucina", w: "bg-red-100 border-red-500", c: "bg-emerald-100 border-emerald-500" },
                            { l: "Bancone bar", w: "bg-amber-100 border-amber-500", c: "bg-cyan-100 border-cyan-500" },
                            { l: "Pizzeria", w: "bg-yellow-100 border-yellow-500", c: "bg-violet-100 border-violet-500" },
                        ].map((z) => (
                            <div key={z.l} className="flex items-center gap-2">
                                <span className="font-medium text-sf-secondary min-w-[80px]">{z.l}</span>
                                <span className={`inline-block w-5 h-3 rounded-sm border ${z.w}`} title="Turno mattina/pranzo (caldo)"></span>
                                <span className={`inline-block w-5 h-3 rounded-sm border ${z.c}`} title="Turno sera (freddo)"></span>
                            </div>
                        ))}
                    </div>
                    <div className="text-sf-muted mt-1.5">
                        🔥 Colori caldi = inizio servizio fino alle 15:59 · ❄️ Colori freddi = dalle 16:00 a chiusura
                    </div>
                </div>
                <div
                    className="grid grid-cols-7 gap-px bg-sf rounded-xl overflow-hidden border border-sf"
                    data-testid="shifts-calendar"
                >
                    {days.map((d, i) => (
                        <div key={i} className="bg-white">
                            <div className="px-3 py-3 border-b border-sf flex items-center justify-between">
                                <div>
                                    <div className="label-eyebrow">
                                        {DAYS[i]}
                                    </div>
                                    <div className="font-display text-2xl text-navy">
                                        {d.getDate()}
                                    </div>
                                </div>
                                <button
                                    onClick={() => openNewAt(fmt(d))}
                                    className="text-amber-brand hover:bg-amber-brand/10 p-1.5 rounded"
                                    data-testid={`shifts-day-add-${fmt(d)}`}
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            <div className="p-2 space-y-2 min-h-[300px]">
                                {visibleShifts
                                    .filter((s) => s.date === fmt(d))
                                    .map((s) => (
                                        <div
                                            key={s.id}
                                            className={`shift-cell rounded-md border px-2 py-1.5 text-xs ${colorForShift(s)}`}
                                            data-testid={`shift-${s.id}`}
                                        >
                                            <div className="font-semibold truncate flex items-center gap-1">
                                                {(() => {
                                                    const e = employees.find((x) => x.id === s.employee_id);
                                                    if (e?.photo_url) {
                                                        return (
                                                            <img
                                                                src={e.photo_url}
                                                                alt=""
                                                                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <span className="text-base leading-none">
                                                            {e?.emoji || "👤"}
                                                        </span>
                                                    );
                                                })()}
                                                <span className="truncate">{s.employee_name}</span>
                                            </div>
                                            <div className="opacity-80">
                                                {s.start_time}–{s.end_time}
                                            </div>
                                            {s.location && (
                                                <div className="opacity-60">
                                                    {s.location}
                                                </div>
                                            )}
                                            <div className="mt-1 flex items-center gap-2">
                                                <button
                                                    onClick={() => openEdit(s)}
                                                    className="opacity-60 hover:opacity-100"
                                                    data-testid={`shift-edit-${s.id}`}
                                                    title="Modifica"
                                                >
                                                    <Pencil size={11} />
                                                </button>
                                                <button
                                                    onClick={() => remove(s.id)}
                                                    className="opacity-60 hover:opacity-100"
                                                    data-testid={`shift-delete-${s.id}`}
                                                    title="Elimina"
                                                >
                                                    <Trash2 size={11} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>
                </>
            ) : (
                <div
                    className="bg-white border border-sf rounded-xl overflow-hidden"
                    data-testid="shifts-table"
                >
                    <table className="w-full text-sm">
                        <thead className="bg-[#fafaf7]">
                            <tr className="text-left">
                                <th className="px-4 py-3 label-eyebrow">
                                    Data
                                </th>
                                <th className="px-4 py-3 label-eyebrow">
                                    Dipendente
                                </th>
                                <th className="px-4 py-3 label-eyebrow">Orario</th>
                                <th className="px-4 py-3 label-eyebrow">
                                    Postazione
                                </th>
                                <th className="px-4 py-3 label-eyebrow text-right">
                                    Azioni
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleShifts.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="text-center py-12 text-sf-muted"
                                    >
                                        Nessun turno questa settimana.
                                    </td>
                                </tr>
                            )}
                            {visibleShifts.map((s) => (
                                <tr
                                    key={s.id}
                                    className="border-t border-sf hover:bg-[#fafaf7]"
                                >
                                    <td className="px-4 py-3 font-mono text-xs">
                                        {s.date}
                                    </td>
                                    <td className="px-4 py-3 font-medium">
                                        {(() => {
                                            const ee = employees.find((x) => x.id === s.employee_id);
                                            if (ee?.photo_url) {
                                                return (
                                                    <img
                                                        src={ee.photo_url}
                                                        alt=""
                                                        className="inline-block w-6 h-6 rounded-full object-cover mr-1.5 align-middle"
                                                    />
                                                );
                                            }
                                            return (
                                                <span className="mr-1.5">
                                                    {ee?.emoji || "👤"}
                                                </span>
                                            );
                                        })()}
                                        {s.employee_name}
                                    </td>
                                    <td className="px-4 py-3">
                                        {s.start_time} – {s.end_time}
                                    </td>
                                    <td className="px-4 py-3 text-sf-secondary">
                                        {s.location}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-2">
                                            <button
                                                onClick={() => openEdit(s)}
                                                className="text-navy hover:text-amber-brand p-1"
                                                data-testid={`shift-row-edit-${s.id}`}
                                                title="Modifica"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                onClick={() => remove(s.id)}
                                                className="text-red-600 hover:text-red-700 p-1"
                                                title="Elimina"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
                <DialogContent
                    data-testid="shifts-restore-dialog"
                    className="max-w-2xl max-h-[80vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl flex items-center gap-2">
                            <RotateCcw size={20} />
                            Ripristina turni precedenti
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-sf-secondary">
                        Sono disponibili gli ultimi {backups.length} backup automatici (creati prima di ogni sovrascrittura). Cliccando &quot;Ripristina&quot; i turni attuali della settimana verranno sostituiti con quelli salvati. Anche questa operazione crea un nuovo backup, quindi è sempre reversibile.
                    </p>
                    <div className="mt-3 space-y-2">
                        {backups.length === 0 && (
                            <div className="text-center text-sm text-sf-muted py-6">
                                Nessun backup disponibile.
                            </div>
                        )}
                        {backups.map((b) => {
                            const label =
                                b.source === "ai_optimize"
                                    ? "Pre-Ottimizzazione AI"
                                    : b.source === "copy_week"
                                      ? "Pre-Copia settimana"
                                      : b.source === "pre_restore"
                                        ? "Pre-Ripristino"
                                        : b.source;
                            const dt = new Date(b.created_at);
                            return (
                                <div
                                    key={b.id}
                                    className="border border-sf rounded-md p-3 flex items-center justify-between hover:bg-[#fafaf7]"
                                    data-testid={`backup-${b.id}`}
                                >
                                    <div>
                                        <div className="font-medium text-navy">
                                            Settimana {b.week_start} → {b.week_end}
                                        </div>
                                        <div className="text-xs text-sf-muted">
                                            {b.shifts_count} turni · {label} ·{" "}
                                            {dt.toLocaleString("it-IT", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={() => restoreBackup(b.id)}
                                        className="bg-navy text-white"
                                        data-testid={`restore-${b.id}`}
                                    >
                                        <RotateCcw size={12} className="mr-1.5" />
                                        Ripristina
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRestoreOpen(false)}>
                            Chiudi
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={optOpen} onOpenChange={setOptOpen}>
                <DialogContent
                    data-testid="shift-optimize-dialog"
                    className="max-w-4xl max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl flex items-center gap-2">
                            <Sparkles size={22} className="text-amber-brand" />
                            Ottimizza pianificazione AI
                        </DialogTitle>
                    </DialogHeader>
                    {optStep === 1 && (
                        <div className="space-y-4">
                            <p className="text-sm text-sf-secondary">
                                Indica i <strong>coperti previsti</strong> per ogni giorno e servizio della settimana
                                <strong> {fmt(weekStart)} → {fmt(addDays(weekStart, 6))}</strong>.
                                L&apos;AI genererà il piano turni col costo più basso rispettando i ruoli necessari.
                            </p>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm border border-sf rounded-md">
                                    <thead className="bg-[#fafaf7]">
                                        <tr>
                                            <th className="px-3 py-2 text-left label-eyebrow">Giorno</th>
                                            <th className="px-3 py-2 text-center label-eyebrow">Colazione<br/><span className="opacity-60 normal-case">07:00-11:00</span></th>
                                            <th className="px-3 py-2 text-center label-eyebrow">Pranzo<br/><span className="opacity-60 normal-case">12:00-15:00</span></th>
                                            <th className="px-3 py-2 text-center label-eyebrow">Aperitivo<br/><span className="opacity-60 normal-case">17:30-20:00</span></th>
                                            <th className="px-3 py-2 text-center label-eyebrow">Cena<br/><span className="opacity-60 normal-case">19:00-24:00</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {days.map((d, i) => {
                                            const k = fmt(d);
                                            const v = optCovers[k] || { colazione: 0, pranzo: 0, aperitivo: 0, cena: 0 };
                                            const dayLabels = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
                                            return (
                                                <tr key={k} className="border-t border-sf">
                                                    <td className="px-3 py-2 font-medium">
                                                        {dayLabels[i]} {d.getDate()}/{d.getMonth() + 1}
                                                    </td>
                                                    {["colazione", "pranzo", "aperitivo", "cena"].map((svc) => (
                                                        <td key={svc} className="px-3 py-1.5">
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                value={v[svc]}
                                                                onChange={(e) =>
                                                                    setOptCovers({
                                                                        ...optCovers,
                                                                        [k]: { ...v, [svc]: parseInt(e.target.value || 0) },
                                                                    })
                                                                }
                                                                className="text-center h-8"
                                                                data-testid={`opt-${k}-${svc}`}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="text-xs text-sf-muted bg-amber-brand/10 border border-amber-brand/30 rounded p-2">
                                Lascia 0 i servizi che non offri quel giorno. L&apos;AI userà i dipendenti col costo orario marginale più basso (dipendenti fissi e &quot;non calcolare ore&quot; sono prioritari = costo 0).
                            </div>
                        </div>
                    )}
                    {optStep === 2 && optResult && (
                        <div className="space-y-4">
                            <div className="bg-navy text-white rounded-lg p-4">
                                <div className="label-eyebrow text-amber-brand mb-1">Piano proposto dall&apos;AI</div>
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                    <div>
                                        <div className="opacity-70">Turni</div>
                                        <div className="font-display text-2xl">{optResult.totals.shifts_count}</div>
                                    </div>
                                    <div>
                                        <div className="opacity-70">Ore totali</div>
                                        <div className="font-display text-2xl">{optResult.totals.hours} h</div>
                                    </div>
                                    <div>
                                        <div className="opacity-70">Costo orario marginale</div>
                                        <div className="font-display text-2xl text-amber-brand">€{optResult.totals.marginal_cost}</div>
                                    </div>
                                </div>
                                {optResult.rationale && (
                                    <div className="mt-3 text-xs opacity-80 italic">&quot;{optResult.rationale}&quot;</div>
                                )}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm border border-sf rounded-md">
                                    <thead className="bg-[#fafaf7]">
                                        <tr>
                                            <th className="px-3 py-2 text-left label-eyebrow">Data</th>
                                            <th className="px-3 py-2 text-left label-eyebrow">Dipendente</th>
                                            <th className="px-3 py-2 text-left label-eyebrow">Ruolo</th>
                                            <th className="px-3 py-2 text-left label-eyebrow">Postazione</th>
                                            <th className="px-3 py-2 text-left label-eyebrow">Orario</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {optResult.shifts.length === 0 && (
                                            <tr><td colSpan={5} className="px-3 py-6 text-center text-sf-muted">Nessun turno generato (coperti insufficienti).</td></tr>
                                        )}
                                        {optResult.shifts.map((s, i) => (
                                            <tr key={i} className="border-t border-sf">
                                                <td className="px-3 py-2">{s.date}</td>
                                                <td className="px-3 py-2 font-medium">{s.employee_name}</td>
                                                <td className="px-3 py-2">{s.role}</td>
                                                <td className="px-3 py-2">{s.location}</td>
                                                <td className="px-3 py-2 font-mono">{s.start_time} - {s.end_time}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className={`flex items-start gap-2 rounded p-3 ${optOverwrite ? "bg-red-50 border border-red-200" : "bg-[#fafaf7] border border-sf"}`}>
                                <input
                                    type="checkbox"
                                    id="opt-overwrite"
                                    checked={optOverwrite}
                                    onChange={(e) => setOptOverwrite(e.target.checked)}
                                    className="w-4 h-4 accent-navy mt-0.5"
                                    data-testid="opt-overwrite"
                                />
                                <label htmlFor="opt-overwrite" className="text-sm cursor-pointer">
                                    <div className={`font-medium ${optOverwrite ? "text-red-700" : "text-navy"}`}>
                                        Sovrascrivi turni esistenti di questa settimana
                                    </div>
                                    <div className="text-xs text-sf-muted mt-0.5">
                                        {optOverwrite
                                            ? `Tutti i turni attuali della settimana ${fmt(weekStart)} → ${fmt(addDays(weekStart, 6))} verranno CANCELLATI (salvati nel backup, ripristinabili). Verranno poi inseriti i ${optResult.shifts.length} turni dell'AI.`
                                            : `Disattivato: i ${optResult.shifts.length} turni dell'AI si aggiungeranno agli esistenti (rischio doppioni).`}
                                    </div>
                                </label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        {optStep === 2 && (
                            <Button variant="outline" onClick={() => setOptStep(1)} disabled={optLoading}>
                                ← Modifica coperti
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => setOptOpen(false)} disabled={optLoading}>
                            Annulla
                        </Button>
                        {optStep === 1 ? (
                            <Button
                                className="bg-amber-brand text-navy hover:bg-amber-brand/90"
                                onClick={generateOptimization}
                                disabled={optLoading}
                                data-testid="opt-generate"
                            >
                                {optLoading ? (
                                    <><Loader2 size={14} className="mr-2 animate-spin" /> Generazione…</>
                                ) : (
                                    <><Sparkles size={14} className="mr-2" /> Genera piano AI</>
                                )}
                            </Button>
                        ) : (
                            <Button
                                className="bg-navy text-white"
                                onClick={applyOptimization}
                                disabled={optLoading || (optResult && optResult.shifts.length === 0)}
                                data-testid="opt-apply"
                            >
                                {optLoading ? (
                                    <><Loader2 size={14} className="mr-2 animate-spin" /> Applico…</>
                                ) : (
                                    <>Applica turni al calendario</>
                                )}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
                <DialogContent data-testid="shift-copy-dialog" className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            Copia settimana
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p className="text-sf-secondary">
                            Stai per copiare <strong>{shifts.length} turni</strong>{" "}
                            dalla settimana{" "}
                            <strong>
                                {fmt(weekStart)} → {fmt(addDays(weekStart, 6))}
                            </strong>{" "}
                            alla settimana successiva (
                            <strong>
                                {fmt(addDays(weekStart, 7))} →{" "}
                                {fmt(addDays(weekStart, 13))}
                            </strong>
                            ).
                        </p>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="copy-overwrite"
                                checked={copyOverwrite}
                                onChange={(e) =>
                                    setCopyOverwrite(e.target.checked)
                                }
                                data-testid="copy-overwrite"
                                className="w-4 h-4 accent-navy"
                            />
                            <label
                                htmlFor="copy-overwrite"
                                className="cursor-pointer"
                            >
                                Sovrascrivi turni esistenti nella settimana di
                                destinazione
                            </label>
                        </div>
                        {!copyOverwrite && (
                            <div className="text-xs text-sf-muted bg-[#fafaf7] rounded p-2">
                                Senza sovrascrittura i turni vengono aggiunti a
                                quelli già presenti.
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setCopyOpen(false)}
                        >
                            Annulla
                        </Button>
                        <Button
                            className="bg-amber-brand text-navy hover:bg-amber-brand/90"
                            onClick={copyWeekToNext}
                            data-testid="shift-copy-confirm"
                        >
                            <Copy size={14} className="mr-2" />
                            Conferma copia
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditForm(null); }}>
                <DialogContent data-testid="shift-edit-dialog" className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            Modifica turno
                        </DialogTitle>
                    </DialogHeader>
                    {editForm && (
                        <div className="space-y-3">
                            <Field label="Dipendente">
                                <Select
                                    value={editForm.employee_id}
                                    onValueChange={(v) =>
                                        setEditForm({ ...editForm, employee_id: v })
                                    }
                                >
                                    <SelectTrigger data-testid="shift-edit-employee">
                                        <SelectValue placeholder="Seleziona…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {employees.map((e) => (
                                            <SelectItem key={e.id} value={e.id}>
                                                {e.name} — {e.role}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Data">
                                <Input
                                    type="date"
                                    value={editForm.date}
                                    onChange={(e) =>
                                        setEditForm({ ...editForm, date: e.target.value })
                                    }
                                    data-testid="shift-edit-date"
                                />
                            </Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Inizio">
                                    <Input
                                        type="time"
                                        value={editForm.start_time}
                                        onChange={(e) =>
                                            setEditForm({ ...editForm, start_time: e.target.value })
                                        }
                                        data-testid="shift-edit-start"
                                    />
                                </Field>
                                <Field label="Fine">
                                    <Input
                                        type="time"
                                        value={editForm.end_time}
                                        onChange={(e) =>
                                            setEditForm({ ...editForm, end_time: e.target.value })
                                        }
                                        data-testid="shift-edit-end"
                                    />
                                </Field>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Ruolo turno">
                                    <Select
                                        value={editForm.role || ""}
                                        onValueChange={(v) =>
                                            setEditForm({ ...editForm, role: v })
                                        }
                                    >
                                        <SelectTrigger data-testid="shift-edit-role">
                                            <SelectValue placeholder="Seleziona ruolo" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SHIFT_ROLES.map((r) => (
                                                <SelectItem key={r} value={r}>
                                                    {r}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Postazione">
                                    <Select
                                        value={editForm.location || "Sala"}
                                        onValueChange={(v) =>
                                            setEditForm({ ...editForm, location: v })
                                        }
                                    >
                                        <SelectTrigger data-testid="shift-edit-location">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SHIFT_LOCATIONS.map((z) => (
                                                <SelectItem key={z} value={z}>
                                                    {z}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>
                            Annulla
                        </Button>
                        <Button
                            className="bg-navy text-white"
                            onClick={saveEdit}
                            data-testid="shift-edit-save"
                        >
                            Salva modifiche
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-wider text-sf-muted">
                {label}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
